<template>
  <div class="rb-ob-form rb-book-page next-session-page">
    <header class="next-session-header">
      <p class="next-session-kicker">{{ $t('intake.portal.nextSessionKicker') }}</p>
      <h2 class="rb-ob-title">{{ $t('intake.portal.nextSessionTitle') }}</h2>
      <p class="next-session-intro">{{ $t('intake.portal.nextSessionIntro') }}</p>
    </header>
    <p v-if="loading" class="rb-ob-status">{{ $t('intake.portal.loading') }}</p>
    <div v-else-if="error" class="rb-ob-status" role="alert">
      <p>{{ error }}</p>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" :disabled="busy" @click="load">{{ lang === 'fa' ? 'بررسی دوباره' : 'Try again' }}</button>
      <router-link class="btn btn-secondary" :to="{name:'portalSupport',params:{lang}}">{{ lang === 'fa' ? 'درخواست پشتیبانی' : 'Contact support' }}</router-link>
    </div>
    <section v-else-if="guidance" class="rb-ob-status" role="status">
      <h3>{{ guidance.title }}</h3><p>{{ guidance.body }}</p>
      <div class="actions">
        <router-link class="btn btn-primary" :to="guidanceRoute">{{ guidance.label }}</router-link>
        <router-link v-if="!episode" class="btn btn-secondary" :to="{name:'portalPayments',params:{lang}}">{{ lang === 'fa' ? 'بررسی پرداخت‌ها' : 'Review payments' }}</router-link>
      </div>
    </section>
    <template v-else-if="model">
      <article v-if="model.therapist" class="next-session-therapist">
        <img v-if="model.therapist.avatar" class="next-session-avatar" :src="model.therapist.avatar" :alt="model.therapist.name">
        <span v-else class="next-session-avatar next-session-avatar--fallback" aria-hidden="true">{{ therapistInitial }}</span>
        <div class="next-session-therapist-copy">
          <p>{{ $t('intake.portal.nextSessionTherapistLabel') }}</p>
          <h3>{{ model.therapist.name }}</h3>
          <span>{{ therapistDetail }}</span>
        </div>
        <span v-if="model.therapist.rating" class="next-session-rating">{{ $t('intake.portal.nextSessionRating', {rating: model.therapist.rating}) }}</span>
      </article>
      <template v-if="model.eligible">
        <p v-if="hold && hold.expires_at" class="rb-ob-status" dir="ltr">{{ countdown }}</p>
        <section v-if="!hold" class="next-session-slots">
          <h3>{{ $t('intake.portal.nextSessionSlotsTitle') }}</h3>
          <p>{{ $t('intake.portal.nextSessionSlotsHint') }}</p>
          <p>{{ $t('intake.bookingUi.timezone', {zone: displayTimezone}) }}</p>
          <div v-if="(model.slots || []).length" class="rb-time-grid next-session-time-grid" role="listbox">
          <label v-for="slot in model.slots || []" :key="slot.id" class="rb-time-card next-session-time-card" :class="{'is-selected': selectedSlotId === String(slot.id)}">
            <input v-model="selectedSlotId" type="radio" name="next-session-slot" :value="String(slot.id)">
            <span class="next-session-slot-day">{{ slotDay(slot) }}</span>
            <bdi class="next-session-slot-time">{{ slotTime(slot) }}</bdi>
          </label>
          </div>
          <div v-else class="rb-ob-status">
            <p>{{ $t('intake.portal.nextSessionNoSlots') }}</p>
            <button class="btn btn-secondary" type="button" :disabled="busy" @click="load">{{ lang === 'fa' ? 'بررسی دوبارهٔ زمان‌ها' : 'Refresh available times' }}</button>
            <router-link class="btn btn-secondary" :to="{name:'portalSupport',params:{lang}}">{{ lang === 'fa' ? 'درخواست پشتیبانی' : 'Contact support' }}</router-link>
          </div>
        </section>
        <p v-else-if="hold.payment_required" class="rb-ob-status">{{ $t('intake.portal.nextSessionPaymentRequired') }}</p>
        <p v-else-if="hold.state === 'CONSUMED'" class="rb-ob-status">{{ $t('intake.portal.nextSessionConfirmed') }}</p>
        <section v-if="requiresPaymentReview" class="next-session-review" aria-labelledby="nextSessionReviewTitle">
          <h3 id="nextSessionReviewTitle">{{ lang === 'fa' ? 'مرور رزرو و پرداخت' : 'Review booking and payment' }}</h3>
          <p v-if="reviewNotice" role="status">{{ reviewNotice }}</p>
          <template v-if="review">
            <dl class="rb-book-summary">
              <div><dt>{{ lang === 'fa' ? 'زمان جلسه' : 'Session time' }}</dt><dd>{{ reviewTime }} · {{ displayTimezone }}</dd></div>
              <div><dt>{{ lang === 'fa' ? 'مبلغ کل بسته' : 'Package total' }}</dt><dd>{{ reviewPrice }}</dd></div>
              <div><dt>{{ lang === 'fa' ? 'تعداد جلسه' : 'Session count' }}</dt><dd>{{ review.price.session_count }}</dd></div>
            </dl>
            <p>{{ cancellationSummary }}</p><p>{{ rescheduleSummary }}</p>
            <label><input v-model="reviewConfirmed" type="checkbox" :disabled="busy || !reviewValid"> {{ lang === 'fa' ? 'زمان، مبلغ و شرایط این رزرو را بررسی کردم.' : 'I have reviewed the time, amount and terms for this booking.' }}</label>
            <p v-if="!reviewValid" role="status">{{ lang === 'fa' ? 'این خلاصه منقضی یا ناقص است. پیش از پرداخت دوباره بررسی کن.' : 'This summary has expired or is incomplete. Refresh it before payment.' }}</p>
          </template>
          <p v-else role="status">{{ lang === 'fa' ? 'خلاصهٔ معتبر پرداخت دریافت نشد. پرداخت تازه‌ای انجام نمی‌شود.' : 'A valid payment summary was not received. A new payment will not be started.' }}</p>
          <button class="btn btn-secondary" type="button" :disabled="busy" @click="load">{{ lang === 'fa' ? 'بررسی دوبارهٔ خلاصه' : 'Refresh summary' }}</button>
        </section>
        <div class="rb-ob-actions">
          <button v-if="!hold" class="rb-ob-submit" :disabled="busy || !selectedSlotId" type="button" @click="createHold">{{ primaryActionLabel }}</button>
          <button v-else-if="hold.credit_available && hold.state === 'HELD'" class="rb-ob-submit" :disabled="busy" type="button" @click="confirmCredit">{{ $t('intake.portal.nextSessionUseCredit') }}</button>
          <template v-else-if="requiresPaymentReview || hold.payment_required">
            <button class="rb-ob-submit" :disabled="busy || (requiresPaymentReview && (!reviewConfirmed || !reviewValid))" type="button" @click="checkout">{{ $t('intake.portal.nextSessionGoToPayment') }}</button>
          </template>
          <button v-if="hold && hold.state !== 'CONSUMED'" class="rb-ob-submit rb-ob-submit-secondary" :disabled="busy" type="button" @click="release">{{ $t('intake.portal.nextSessionRelease') }}</button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import {
  checkoutNextSession,
  confirmNextSessionCredit,
  fetchActiveCare,
  fetchNextSessionBooking,
  fetchProfile,
  getCachedProfile,
  holdNextSession,
  reserveNextSessionCredit,
  releaseNextSessionHold,
  formatMoneyAmount,
} from '@/intake/portalApi'
import {redirectToPayment} from '@/intake/paymentNavigation'
import {clientError} from '@/intake/clientMessages'
import {nextSessionGuidance} from '@/intake/bookingRecovery'

export default {
  name: 'PortalNextSessionBookingStep',
  data() {
    return {loading: true, busy: false, error: '', episode: null, model: null, hold: null, profile: getCachedProfile(), selectedSlotId: '', now: Date.now(), timer: null, generation: 0, reviewConfirmed: false, reviewNotice: ''}
  },
  computed: {
    lang() { return this.$route.params.lang || 'fa' },
    guidance() {
      if (this.loading || this.error || (this.episode && this.model && this.model.eligible)) return null
      return nextSessionGuidance(this.episode, this.model, this.lang)
    },
    guidanceRoute() {
      const guidance = this.guidance
      return guidance ? {name:guidance.route, params:{lang:this.lang}, query:guidance.query || {}} : null
    },
    displayTimezone() {
      const candidates = [this.model && this.model.display_timezone, this.profile && this.profile.timezone, this.profile && this.profile.tz]
      for (const zone of candidates) {
        if (!zone) continue
        try { new Intl.DateTimeFormat('en', {timeZone: zone}); return zone } catch (_) { /* Try the next configured display zone. */ }
      }
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    },
    therapistInitial() {
      const name = (this.model && this.model.therapist && this.model.therapist.name) || '—'
      return name.trim().split(/\s+/).map((part) => part[0]).join('').slice(0, 2)
    },
    therapistDetail() {
      const therapist = this.model && this.model.therapist
      if (!therapist) return ''
      const details = [therapist.subtitle, therapist.major_field]
      if (therapist.years_of_experience) details.push(this.$t('intake.portal.nextSessionExperience', {years: therapist.years_of_experience}))
      return details.filter(Boolean).join(' · ') || this.$t('intake.portal.therapistRole')
    },
    primaryActionLabel() {
      return this.model && this.model.available_credit
        ? this.$t('intake.portal.nextSessionReserve')
        : this.$t('intake.portal.nextSessionChooseAndPay')
    },
    requiresPaymentReview() { return !!this.hold && ['HELD','PAYMENT_REQUIRED'].includes(this.hold.state) && !this.hold.credit_available && !this.hold.cart_id },
    review() { return this.hold && this.hold.review || null },
    reviewValid() {
      const r=this.review
      return !!(r && this.hold && r.hold_id === this.hold.id && /^[a-f\d]{64}$/i.test(r.review_id || '')
        && r.slot && String(r.slot.id) === String(this.hold.slot_id) && this.slotDate({from_time:r.slot.starts_at})
        && r.price && Number.isFinite(Number(r.price.amount)) && Number(r.price.amount)>0 && r.price.currency
        && r.policy && r.policy.cancellation && r.policy.reschedule && new Date(r.valid_until).getTime()>this.now)
    },
    reviewTime() { return this.review && this.review.slot ? this.slotDay({from_time:this.review.slot.starts_at})+' · '+this.slotTime({from_time:this.review.slot.starts_at}) : '—' },
    reviewPrice() { return this.review && this.review.price ? formatMoneyAmount(this.review.price.amount,this.review.price.currency,this.lang) : '—' },
    cancellationSummary() {
      const p=this.review && this.review.policy && this.review.policy.cancellation
      if(!p)return ''
      const hours=p.deadline_hours_before_session
      return this.lang==='fa' ? `لغو طبق سیاست رزرو تا ${hours} ساعت پیش از جلسه مجاز است. انتقال ارزش: ${this.valueTransferLabel(p.value_transfer)}. بازپرداخت نقدی: ${this.cashRefundLabel(p.cash_refund)}.` : `Cancellation is allowed under this booking policy until ${hours} hours before the session. Value transfer: ${this.valueTransferLabel(p.value_transfer)}. Cash refund: ${this.cashRefundLabel(p.cash_refund)}.`
    },
    rescheduleSummary() {
      const p=this.review && this.review.policy && this.review.policy.reschedule
      if(!p)return ''
      const fee=formatMoneyAmount(p.fee_amount,p.fee_currency || this.review.price.currency,this.lang)
      return this.lang==='fa' ? `تغییر زمان تا ${p.deadline_hours_before_session} ساعت پیش از جلسه؛ حداکثر ${p.max_changes} بار. هزینهٔ تغییر: ${fee}.` : `Reschedule until ${p.deadline_hours_before_session} hours before the session; up to ${p.max_changes} changes. Change fee: ${fee}.`
    },
    countdown() {
      if (!this.hold || !this.hold.expires_at) return ''
      const seconds = Math.max(0, Math.ceil((new Date(this.hold.expires_at).getTime() - this.now) / 1000))
      const value = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
      return this.$t('intake.portal.nextSessionHoldUntil', {time: value})
    },
  },
  mounted() {
    this.timer = window.setInterval(() => { this.now = Date.now() }, 1000)
    this.load()
  },
  beforeDestroy() { this.generation += 1; if (this.timer) window.clearInterval(this.timer) },
  methods: {
    valueTransferLabel(value) {
      const code=String(value || '').toUpperCase()
      if(/CREDIT|RESTORE/.test(code))return this.lang==='fa'?'اعتبار جلسه':'session credit'
      if(/NONE|NO_TRANSFER/.test(code))return this.lang==='fa'?'ندارد':'none'
      return this.lang==='fa'?'طبق سیاست ثبت‌شدهٔ رزرو':'according to the recorded booking policy'
    },
    cashRefundLabel(value) {
      if(value===false || /NONE|DISABLED|UNAVAILABLE|NOT_SUPPORTED|NO_REFUND/.test(String(value).toUpperCase()))return this.lang==='fa'?'ندارد':'none'
      return this.lang==='fa'?'طبق سیاست ثبت‌شده؛ بازپرداخت خودکار ادعا نمی‌شود':'according to the recorded policy; no automatic refund is promised'
    },
    handleReservationResult(hold) {
      this.hold = hold
      if (hold && hold.state === 'CONSUMED' && hold.user_session_id) {
        return this.$router.push({name: 'portalSessions', params: {lang: this.lang}})
      }
      return this.load()
    },
    load() {
      if (this.loading && this.generation) return
      const generation = ++this.generation
      this.loading = true; this.error = ''; this.model = null; this.hold = null; this.episode = null; this.selectedSlotId = ''
      this.reviewConfirmed = false; this.reviewNotice = ''
      return Promise.all([fetchActiveCare(), fetchProfile().catch(() => getCachedProfile())]).then(([episode, profile]) => {
        if (generation !== this.generation) return null
        this.profile = profile
        this.episode = episode
        if (!episode || !episode.id) return null
        return fetchNextSessionBooking(episode.id)
      }).then((data) => {
        if (generation !== this.generation) return
        this.model = data || null
        this.hold = data && data.hold ? data.hold : null
      }).catch((e) => { if (generation === this.generation) this.error = clientError(e, this.lang) })
        .finally(() => { if (generation === this.generation) this.loading = false })
    },
    createHold() {
      if (this.busy) return
      this.busy = true; this.error = ''
      const key = `next-session-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const reserve = this.model && this.model.available_credit
        ? reserveNextSessionCredit(this.episode.id, Number(this.selectedSlotId), key)
        : holdNextSession(this.episode.id, Number(this.selectedSlotId), key)
      return reserve
        .then((hold) => {
          this.hold = hold
          this.reviewConfirmed = false
          if (!this.model.available_credit && hold && hold.id && hold.state !== 'CONSUMED') return
          return this.handleReservationResult(hold)
        })
        .catch((e) => { this.error = clientError(e, this.lang) })
        .finally(() => { this.busy = false })
    },
    confirmCredit() {
      if (this.busy) return
      this.busy = true; this.error = ''
      return confirmNextSessionCredit(this.episode.id, this.hold.id)
        .then((hold) => this.handleReservationResult(hold))
        .catch((e) => { this.error = clientError(e, this.lang) })
        .finally(() => { this.busy = false })
    },
    checkout() {
      if (this.busy || !this.hold || !this.episode || (this.requiresPaymentReview && (!this.reviewConfirmed || !this.reviewValid))) return
      this.busy = true; this.error = ''
      return checkoutNextSession(this.episode.id, this.hold.id, this.review && this.review.review_id)
        .then((hold) => {
          this.hold = hold
          if (hold.state === 'CONSUMED' && hold.user_session_id) return this.handleReservationResult(hold)
          if (redirectToPayment(hold.payment_url)) {
            return
          }
          return this.$router.push({name: 'portalNextSessionPayReturn', params: {lang: this.lang}, query: {episode_id: this.episode.id, hold_id: hold.id}})
        })
        .catch((e) => {
          const code=e && ((e.data && (e.data.code || e.data.reason_code)) || e.code || (e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code))
          if(code==='NEXT_SESSION_REVIEW_CHANGED')return this.load().then(()=>{this.reviewNotice=this.lang==='fa'?'مبلغ یا شرایط تغییر کرده است. خلاصهٔ تازه را بررسی و دوباره تأیید کن.':'The amount or terms changed. Review the new summary and confirm again.'})
          this.error = clientError(e, this.lang)
        })
        .finally(() => { this.busy = false })
    },
    release() {
      if (this.busy) return
      this.busy = true
      return releaseNextSessionHold(this.episode.id, this.hold.id)
        .then(() => { this.hold = null; return this.load() })
        .catch((e) => { this.error = clientError(e, this.lang) })
        .finally(() => { this.busy = false })
    },
    slotDate(slot) {
      const raw = String((slot && slot.from_time) || '').trim().replace(' ', 'T')
      if (!raw) return null
      // Overview exposes raw UTC times; preserve an explicit timezone when present.
      const value = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : raw + 'Z'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    },
    slotDay(slot) {
      const date = this.slotDate(slot)
      return date ? date.toLocaleDateString(this.lang === 'fa' ? 'fa-IR' : 'en-US', {weekday: 'long', month: 'long', day: 'numeric', timeZone: this.displayTimezone}) : (slot && slot.from_time) || ''
    },
    slotTime(slot) {
      const date = this.slotDate(slot)
      return date ? date.toLocaleTimeString(this.lang === 'fa' ? 'fa-IR' : 'en-US', {hour: '2-digit', minute: '2-digit', timeZone: this.displayTimezone}) : ''
    },
  },
}
</script>

<style scoped>
.next-session-page {
  max-width: 46rem;
  margin: 0 auto;
}
.next-session-header {
  text-align: center;
  margin-bottom: 1.5rem;
}
.next-session-kicker {
  margin: 0 0 .4rem;
  color: #179f98;
  font-size: .8rem;
  font-weight: 700;
}
.next-session-intro {
  margin: .5rem auto 0;
  color: #637783;
  max-width: 30rem;
  line-height: 1.8;
}
.next-session-therapist {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1rem;
  border: 1px solid #dce9e7;
  border-radius: 1rem;
  background: #f7fbfa;
  margin-bottom: 1.25rem;
}
.next-session-avatar {
  width: 3.25rem;
  height: 3.25rem;
  object-fit: cover;
  border-radius: 50%;
  flex: 0 0 auto;
}
.next-session-avatar--fallback {
  display: grid;
  place-items: center;
  background: #dff5f1;
  color: #11665f;
  font-weight: 800;
}
.next-session-therapist-copy { min-width: 0; flex: 1; }
.next-session-therapist-copy p,
.next-session-therapist-copy h3,
.next-session-therapist-copy span { margin: 0; }
.next-session-therapist-copy p { color: #6b7f88; font-size: .77rem; }
.next-session-therapist-copy h3 { color: #103f57; margin: .15rem 0; font-size: 1rem; }
.next-session-therapist-copy span { color: #526e7c; font-size: .78rem; }
.next-session-rating {
  color: #9b6b00;
  background: #fff7de;
  border-radius: 999px;
  padding: .35rem .55rem;
  font-size: .72rem;
  white-space: nowrap;
}
.next-session-slots h3 { color: #103f57; margin: 0; font-size: 1rem; }
.next-session-slots > p { color: #687d87; font-size: .82rem; margin: .35rem 0 1rem; }
.next-session-time-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .7rem;
}
.next-session-time-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: .35rem;
  min-height: 5.25rem;
  padding: .85rem;
  border: 1px solid #d9e5e7;
  border-radius: .9rem;
  background: #fff;
  color: #123f56;
  cursor: pointer;
}
.next-session-time-card.is-selected {
  border-color: #18a79d;
  background: #e9f9f7;
  box-shadow: 0 0 0 2px rgb(24 167 157 / .12);
}
.next-session-time-card input { position: absolute; opacity: 0; }
.next-session-slot-day { color: #617883; font-size: .75rem; }
.next-session-slot-time { color: #123f56; font-size: 1rem; font-weight: 800; direction: ltr; }
@media (min-width: 42rem) {
  .next-session-time-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
</style>
